import {
  MIN_KRS_PER_OBJECTIVE,
  MAX_KRS_PER_OBJECTIVE,
  type Familiarity,
} from "@itspip/shared";
import type {
  CoachMessage,
  KrTypeInput,
  ObjectiveLevel,
  ObjectiveType,
} from "@/hooks/use-coach";

export type Quarter = "Q1" | "Q2" | "Q3" | "Q4" | "annual";
export type KrQuarter = "Q1" | "Q2" | "Q3" | "Q4";

export const MAX_COACHING_ROUNDS = 5;
export const MIN_KRS = MIN_KRS_PER_OBJECTIVE;
export const MAX_KRS = MAX_KRS_PER_OBJECTIVE;

export interface WizardKrDraft {
  localId: string;
  title: string;
  type: KrTypeInput;
  targetValue: string;
  quarter: KrQuarter;
  dueDate: string;
  /** Streamed feedback from the last "Coach This KR" run. Not persisted. */
  coachFeedback: string;
}

/**
 * Shape of state passed between wizard steps.
 * Step 1 fills `setup`, Step 2 fills `objective`, Step 3 fills `keyResults`.
 */
export interface WizardState {
  setup: {
    level: ObjectiveLevel | null;
    type: ObjectiveType | null;
    year: number;
    quarter: Quarter;
    parentObjectiveId: string | null;
    focusAreaId: string | null;
    tacticId: string | null;
    /**
     * Per-session familiarity override. Null means the server falls back to
     * the user's persisted record. A user-confirmed change PATCHes the server
     * separately (see setup-step).
     */
    familiarity: Familiarity | null;
  };
  objective: {
    statement: string;
    /** User/assistant exchanges from completed rounds, sent to the API as prior_feedback. */
    history: readonly CoachMessage[];
    /** Number of completed coaching rounds for the objective. */
    roundCount: number;
    /** Original draft captured on first coaching request (for reflection turn). */
    firstDraft: string;
    /** Set once the metacognitive reflection has been submitted. */
    reflectionSubmitted: boolean;
  };
  keyResults: WizardKrDraft[];
  krs: {
    history: readonly CoachMessage[];
    /** First-draft snapshot of the KR set titles, joined with newlines. */
    firstDraft: string;
    reflectionSubmitted: boolean;
  };
}

export function isObjectiveStepComplete(state: WizardState): boolean {
  return (
    state.objective.statement.trim().length > 0 && state.objective.roundCount > 0
  );
}

export function createEmptyKr(quarter: KrQuarter = "Q1"): WizardKrDraft {
  return {
    localId: crypto.randomUUID(),
    title: "",
    type: "percentage",
    targetValue: "",
    quarter,
    dueDate: "",
    coachFeedback: "",
  };
}

function defaultKrQuarter(setupQuarter: Quarter): KrQuarter {
  return setupQuarter === "annual" ? "Q1" : setupQuarter;
}

export function createInitialState(currentYear: number): WizardState {
  const setupQuarter: Quarter = "annual";
  return {
    setup: {
      level: null,
      type: null,
      year: currentYear,
      quarter: setupQuarter,
      parentObjectiveId: null,
      focusAreaId: null,
      tacticId: null,
      familiarity: null,
    },
    objective: {
      statement: "",
      history: [],
      roundCount: 0,
      firstDraft: "",
      reflectionSubmitted: false,
    },
    keyResults: [
      createEmptyKr(defaultKrQuarter(setupQuarter)),
      createEmptyKr(defaultKrQuarter(setupQuarter)),
    ],
    krs: { history: [], firstDraft: "", reflectionSubmitted: false },
  };
}

export const WIZARD_STEPS = [
  { label: "Set Up" },
  { label: "Write Objective" },
  { label: "Key Results & Save" },
] as const;
